<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/api/index';
import { DgrSelectbox } from '@stockmarkteam/donguri-ui';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import Content from '@/components/layouts/content.vue';
import Header from '@/components/layouts/header.vue';
import { MypageAccessScope } from '@/types';
import { useUserSettingsProfile } from '@/utils/swr';

const route = useRoute();
const { createSnackbar } = useSnackbar();

const { data: userSettings, mutate } = useUserSettingsProfile();

const selectedScope = ref<MypageAccessScope>('public');

const PAGE_NAME = 'profile';

watch(
  () => userSettings.value,
  () => {
    if (!userSettings.value) return;
    selectedScope.value = userSettings.value.mypage_access_scope;
  },
  { immediate: true },
);

const scopes = computed<{ label: string; value: MypageAccessScope }[]>(() => {
  if (!userSettings.value) return [];
  return [
    { label: '全体', value: 'public' },
    { label: '自分のみ', value: 'personal' },
  ];
});

const submit = async () => {
  if (!userSettings.value) return;
  try {
    /**
     * プロフィールの画面と同じキャッシュなので、書き換えずに送る。
     * 書き換えると、保存に失敗したときにプロフィールの保存で未保存の値まで送られてしまう。
     */
    await api.updateUserSettingsProfile(
      { ...userSettings.value, mypage_access_scope: selectedScope.value },
      {
        pageName: PAGE_NAME,
        pageUrl: route.fullPath,
      },
    );
    mutate();

    createSnackbar({
      message: '設定を更新しました',
      type: 'success',
    });
  } catch (e) {
    createSnackbar({
      message: '設定を更新できませんでした',
      type: 'error',
    });
    throw e;
  }
};
</script>
<template>
  <div class="user-settings" v-if="userSettings">
    <Header title="マーク"></Header>
    <Content>
      <div class="settings-card">
        <div class="c-formBlock">
          <div class="c-formBlock__label">マークの公開範囲</div>
          <div class="description c-text-regular--s">
            自分がマークをした記事の公開範囲を指定することができます。
          </div>
          <DgrSelectbox
            class="scope-select"
            :options="scopes"
            v-model="selectedScope"
            options-max-height="240px"
          ></DgrSelectbox>
        </div>
      </div>
    </Content>
    <div class="settings-footer">
      <button
        class="c-btn c-btn--small c-btn--auto c-btn--AnewsPrimary"
        @click="submit"
      >
        保存
      </button>
    </div>
  </div>
</template>
<style lang="scss" scoped>
// 内容が短くても画面の最下部に置く
.settings-footer {
  position: sticky;
  bottom: 0;
  margin-top: auto;
  padding: 16px 24px;
  background: $color-white;
  box-shadow: 0px 1px 5px rgba($color-gray1000, 0.25);
}

.user-settings {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  // コンテンツを内容領域いっぱいに広げる（Header・Contentの幅もこの変数で決まる）
  --layout-content-width: 100%;

  .settings-card {
    box-sizing: border-box;
    max-width: 1248px;
    margin: 0 24px 24px;
    padding: 16px;
    background: $color-white;
    border: 1px solid $color-border;
    border-radius: $border-radius;

    .description {
      margin-bottom: 4px;
      color: $color-gray600;
    }

    .scope-select {
      display: block;
      width: 273px;
    }
  }
}
</style>
